import { ComponentProps, FC, useMemo } from 'react';

import { BsFillPencilFill } from 'react-icons/bs';
import MultiSelect, {
  Option,
} from 'ui/components/form/MultiSelect/MultiSelect';
import { cn } from 'utils/helpers';
import useModal from 'ui/partials/Modal/useModal';
import { Formik, useFormikContext } from 'formik';
import { type FormikHelpers } from 'formik';
import * as Yup from 'yup';

import Tag from '../Tag';
import Button from '../Button';

type VoidFunc = (val: string[]) => void;
type StringFunc = (val: string[]) => string;

type RuleType = VoidFunc | StringFunc;

interface PROPS {
  title: string;
  values?: string[];
  rule?: RuleType;
  options: Option[];
  onSubmit: (val: string[], onSuccess: () => void) => void;
  isLoading?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
}

const QUICK_EDIT_MULTISELECT_SCHEMA = Yup.object({
  isRequired: Yup.boolean().optional().default(false),
  values: Yup.array()
    .of(Yup.string().required())
    .when('isRequired', ([isRequired], schema) => {
      return isRequired
        ? schema.required().min(1, 'Field must have at least 1 item')
        : schema.optional();
    }),
});

export type QUICK_EDIT_MULTISELECT_FORM = Yup.InferType<
  typeof QUICK_EDIT_MULTISELECT_SCHEMA
>;

const QuickEditMultiselect: FC<PROPS> = ({
  isRequired,
  title,
  values = [],
  onSubmit: _onSubmit,
  options,
  isLoading,
  disabled,
}) => {
  const { showModal } = useModal();

  const onSubmit = (
    values: QUICK_EDIT_MULTISELECT_FORM,
    { setSubmitting }: FormikHelpers<QUICK_EDIT_MULTISELECT_FORM>,
  ) => {
    const castValues = QUICK_EDIT_MULTISELECT_SCHEMA.cast(values);
    _onSubmit(castValues.values ?? [], () => {
      setSubmitting(false);
    });
  };

  const handleOpenEditModal = () => {
    showModal({
      title: `Edit ${title}`,
      content: (
        <Formik<QUICK_EDIT_MULTISELECT_FORM>
          validationSchema={QUICK_EDIT_MULTISELECT_SCHEMA}
          initialValues={{
            isRequired: isRequired || false,
            values,
          }}
          onSubmit={onSubmit}
        >
          {({ setFieldValue, errors }) => (
            <QuickEditMultiselectForm
              onChange={(val) => {
                setFieldValue(
                  'values',
                  val.map((opt) => opt.value),
                );
              }}
              textType={errors.values ? 'danger' : 'default'}
              subText={errors.values ? errors.values : undefined}
              defaultValue={values}
              required={isRequired}
              label={title}
              options={options}
            />
          )}
        </Formik>
      ),
    });
  };

  const noValue = useMemo(() => {
    return `no ${title} yet`;
  }, [title]);

  return (
    <div className="border-b-[0.5px] border-b-separator-primary py-[14px]">
      <p className="text-sm secondary text-content">{title}</p>
      <div className="flex items-center gap-1 text-sm group">
        <div
          className={cn(
            !values?.length && 'text-disabled italic lowercase',
            'flex gap-1 flex-wrap',
          )}
        >
          {values?.length
            ? options
                .filter((opt) => values.includes(opt.value))
                .map((opt) => <Tag key={opt.value} label={opt.label} />)
            : noValue}
        </div>
        <button
          disabled={isLoading}
          type="button"
          className={cn(
            !disabled ? 'block' : 'hidden',
            'group-hover:block hidden',
          )}
          onClick={handleOpenEditModal}
        >
          <BsFillPencilFill className="w-3 h-3 text-secondary-base" />
        </button>
      </div>
    </div>
  );
};

export default QuickEditMultiselect;

const QuickEditMultiselectForm: FC<ComponentProps<typeof MultiSelect>> = (
  props,
) => {
  const { hideModal } = useModal();
  const { isSubmitting, handleSubmit } = useFormikContext();
  return (
    <form onSubmit={handleSubmit}>
      <MultiSelect {...props} />
      <div
        className={cn(
          'mt-5 pt-5',
          'flex gap-3 justify-end',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={hideModal}
          type="button"
          disabled={isSubmitting}
          label="CANCEL"
          intent="tertiary"
          size="sm"
        />
        <Button disabled={isSubmitting} label="SAVE" type="submit" size="sm" />
      </div>
    </form>
  );
};
